import { User } from "@/features/users";
import { User as UserType } from "@/entities/user";
import { createApi } from "@reduxjs/toolkit/query/react";
import { Tarif } from "@/widgets";
import {
  AdminChatPayload,
  AdminChatUser,
  ChatMessage,
} from "@/features/chats/model/types";
import { createBaseQuery } from "@/shared/utils/baseQuery";

type SendMessageRequest = {
  user_id: number;
  msg?: string;
  image?: File;
};

type SendMessageResponse = {
  status: number;
  environment: string;
};

type GetChatRequest = {
  user_id: number;
};

type EditChatRequest = {
  user_id: number;
  data: {
    stage?: string;
    coment?: string;
  };
};

type EditChatResponse = {
  success: boolean;
};

type GetChatMessagesRequest = {
  user_id: number;
};

type GetUserActionsRequest = {
  user_id: number;
};

type GetUserActionsResponse = {
  user: {
    user_id: number;
    username: string;
    fullname: string;
  };
  actions: Record<string, any>[];
};

type GetUserResponse = UserType;

type SelectSampleRequest = {
  sample_id: number;
};

type SelectSampleResponse = {
  error?: any;
  sample_id: number;
};

type RefreshSamplesRequest = {
  userMsg?: string;
};

type RefreshSamplesResponse = {
  comment: string;
  task_id: string;
};

type GetAdaptiveStatusRequest = {
  task_id: string;
};

type GetAdaptiveStatusResponse = {
  task_id: string;
  status: 'runing' | 'success' | 'error';
};

type AdminRefreshSamplesRequest = {
  userId: number;
  userMsg?: string;
};

type AdminRefreshSamplesResponse = {
  interpretation: string;
};

type StepData = {
  step: string;
  users_count: number;
};

export enum AnalyticPeriod {
  All = "all",
  Week = "week",
  Month = "month",
  ThreeMonths = "threeMonths",
  SixMonths = "sixMonths",
  Year = "year",
}

type GetAnalyticRequest = {
  period: AnalyticPeriod;
  start_date?: string;
  end_date?: string;
};

type GetAnalyticResponse = StepData[];

type QuizFindAnswerRequest = {
  data_type: Array<'age' | 'height' | 'weight' | 'gender' | 'additional_questions' | 'current_fat_percentage' | 'desired_fat_percentage' | 'required_reviews' | 'required_interpretation' | 'accent' | 'injuries' | 'training_duration'>;
};

type QuizFindAnswerResponse = {
  user_id?: number;
  answers?: Record<string, string | number | boolean>;
};

type CalculateWeightLossDateRequest = {
  age: number;
  weight: number;
  height: number;
  current_fat_percentage: number;
  desired_fat_percentage: number;
};

type CalculateWeightLossDateResponse = {
  date_to_goal?: string | null;
  muscle_gain?: number | null;
  target_weight?: number | null;
};

type AddUserActionRequest = {
  action: string;
};

type AddUserActionResponse = {
  success: boolean;
};

type GenerateAIMessageRequest = {
  message: string;
  conversation_id?: string | null;
  context?: string | null;
};

type GenerateAIMessageResponse = {
  agent: 'consult' | 'generate' | 'unknown';
  result: {
    answer_message?: string;
    conversation_id?: string;
    sample_id?: number;
    why_is_that_so?: string;
  };
  conversation_id?: string;
};

type AIChatMessage = {
  chat_id: number;
  user_id: number;
  author: string;
  content: string;
  of_date: string;
};

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: createBaseQuery("https://api.fitkeep.ru/"),
  tagTypes: ["Users", "Samples"],
  endpoints: (builder) => ({
    getUsers: builder.mutation<{ users: User[] }, void>({
      query: () => ({
        url: "/admin/users",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    sendMessage: builder.mutation<SendMessageResponse, SendMessageRequest>({
      query: (body) => {
        const formData = new FormData();
        formData.append("user_id", body.user_id.toString());
        if (body.msg) {
          formData.append("msg", body.msg);
        }
        if (body.image) {
          formData.append("image", body.image);
        }
        
        return {
          url: "/admin/users/sendmessage",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Users"],
    }),
    getChats: builder.mutation<AdminChatUser[], void>({
      query: () => ({
        url: "/admin/chats",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    getChat: builder.mutation<AdminChatPayload, GetChatRequest>({
      query: ({ user_id }) => ({
        url: `/admin/chats/${user_id}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    getChatMessages: builder.mutation<ChatMessage[], GetChatMessagesRequest>({
      query: ({ user_id }) => ({
        url: `/admin/chats/${user_id}/messages`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    editChat: builder.mutation<EditChatResponse, EditChatRequest>({
      query: ({ user_id, data }) => ({
        url: `/admin/chats/${user_id}/edit`,
        method: "POST",
        body: { data },
      }),
      invalidatesTags: ["Users"],
    }),
    getTrainerChats: builder.mutation<AdminChatUser[], void>({
      query: () => ({
        url: "/trainer/chats",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    getTrainerChat: builder.mutation<AdminChatPayload, GetChatRequest>({
      query: ({ user_id }) => ({
        url: `/trainer/chats/${user_id}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    getTrainerChatMessages: builder.mutation<ChatMessage[], GetChatMessagesRequest>({
      query: ({ user_id }) => ({
        url: `/trainer/chats/${user_id}/messages`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    getUserActions: builder.mutation<
      GetUserActionsResponse,
      GetUserActionsRequest
    >({
      query: (body) => ({
        url: "/admin/users/actions",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    getUser: builder.mutation<GetUserResponse, void>({
      query: () => ({
        url: "/user",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    selectSample: builder.mutation<SelectSampleResponse, SelectSampleRequest>({
      query: (body) => ({
        url: "/samples/select",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Samples"],
    }),
    refreshSamples: builder.mutation<
      RefreshSamplesResponse,
      RefreshSamplesRequest | void
    >({
      query: (body = {}) => ({
        url: "/samples/adaptive?json=1",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Samples"],
    }),
    getAnalytic: builder.mutation<GetAnalyticResponse, GetAnalyticRequest>({
      query: (body) => ({
        url: "/admin/users/analitic",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    quizFindAnswer: builder.mutation<QuizFindAnswerResponse, QuizFindAnswerRequest>({
      query: (body) => ({
        url: "/quiz/answer/find",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    calculateWeightLossDate: builder.mutation<CalculateWeightLossDateResponse, CalculateWeightLossDateRequest>({
      query: (body) => ({
        url: "/calculate/weight_loss_date",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    addUserAction: builder.mutation<AddUserActionResponse, AddUserActionRequest>({
      query: (body) => ({
        url: "/user/actions/add",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    adminRefreshSamples: builder.mutation<AdminRefreshSamplesResponse, AdminRefreshSamplesRequest>({
      query: ({ userId, userMsg }) => ({
        url: `/samples/adaptive?json=1&from_user=${userId}`,
        method: "POST",
        body: { userMsg: userMsg || "" },
      }),
      invalidatesTags: ["Samples"],
    }),
    getAdaptiveStatus: builder.query<GetAdaptiveStatusResponse, GetAdaptiveStatusRequest>({
      query: ({ task_id }) => ({
        url: `/samples/adaptive/${task_id}/status`,
        method: "GET",
      }),
    }),
    generateAIMessage: builder.mutation<GenerateAIMessageResponse, GenerateAIMessageRequest>({
      query: (body) => ({
        url: "/user/ai/chat/generate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    getAIChatMessages: builder.mutation<AIChatMessage[], void>({
      query: () => ({
        url: "/user/ai/chat/messages",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Users"],
    }),
    sendPushNotification: builder.mutation<
      { sent: number; failed: number },
      { title: string; body: string; userIds?: number[]; allUsers?: boolean }
    >({
      query: (body) => ({
        url: "/admin/notifications/send",
        method: "POST",
        body,
      }),
    }),
    getNotificationHistory: builder.query<
      Array<{ id: number; title: string; body: string; sentCount: number; failedCount: number; createdAt: string }>,
      void
    >({
      query: () => ({
        url: "/admin/notifications/history",
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetUsersMutation,
  useSendMessageMutation,
  useGetChatsMutation,
  useGetChatMutation,
  useGetChatMessagesMutation,
  useEditChatMutation,
  useGetUserActionsMutation,
  useGetUserMutation,
  useSelectSampleMutation,
  useRefreshSamplesMutation,
  useGetAnalyticMutation,
  useQuizFindAnswerMutation,
  useCalculateWeightLossDateMutation,
  useAddUserActionMutation,
  useAdminRefreshSamplesMutation,
  useGetAdaptiveStatusQuery,
  useLazyGetAdaptiveStatusQuery,
  useGetTrainerChatsMutation,
  useGetTrainerChatMutation,
  useGetTrainerChatMessagesMutation,
  useGenerateAIMessageMutation,
  useGetAIChatMessagesMutation,
  useSendPushNotificationMutation,
  useGetNotificationHistoryQuery,
} = usersApi;

